import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, User, Phone, MessageSquare, CalendarDays } from 'lucide-react';
import { Lead } from '../../types/lead';
import { StateBadge, IcpBadge } from '../ui/Badge';
interface LeadsTableProps {
  leads: Lead[];
}
export function LeadsTable({
  leads
}: LeadsTableProps) {
  const navigate = useNavigate();
  const maskPhone = (phone: string) => {
    if (!phone) return '';
    const prefix = phone.substring(0, 3);
    const suffix = phone.substring(phone.length - 2);
    return `${prefix} *** ** ${suffix}`;
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short'
    });
  };
  return <div className="card overflow-hidden" data-id="element-413">
      <div className="overflow-x-auto" data-id="element-414">
        <table className="min-w-full divide-y divide-white/5" data-id="element-415">
          <thead className="bg-growthly-panel" data-id="element-416">
            <tr data-id="element-417">
              <th scope="col" className="px-4 py-3 text-left text-[11px] font-medium text-growthly-muted uppercase tracking-wider w-10" data-id="element-418">
                <input type="checkbox" className="rounded border-white/20 bg-growthly-bg text-growthly-amber focus:ring-growthly-amber" data-id="element-419" />
              </th>
              <th scope="col" className="px-4 py-3 text-left text-[11px] font-medium text-growthly-muted uppercase tracking-wider" data-id="element-420">
                Negocio
              </th>
              <th scope="col" className="px-4 py-3 text-left text-[11px] font-medium text-growthly-muted uppercase tracking-wider" data-id="element-421">
                Contacto
              </th>
              <th scope="col" className="px-4 py-3 text-left text-[11px] font-medium text-growthly-muted uppercase tracking-wider" data-id="element-422">
                ICP / Score
              </th>
              <th scope="col" className="px-4 py-3 text-left text-[11px] font-medium text-growthly-muted uppercase tracking-wider" data-id="element-423">
                Estado
              </th>
              <th scope="col" className="px-4 py-3 text-left text-[11px] font-medium text-growthly-muted uppercase tracking-wider" data-id="element-424">
                Próx. Acción
              </th>
              <th scope="col" className="px-4 py-3 text-left text-[11px] font-medium text-growthly-muted uppercase tracking-wider" data-id="element-425">
                Evidencia
              </th>
            </tr>
          </thead>
          <tbody className="bg-growthly-surface divide-y divide-white/5" data-id="element-426">
            {leads.map(lead => <tr key={lead.id} className="h-[72px] hover:bg-white/[0.02] transition-colors cursor-pointer group" onClick={() => navigate(`/leads/${lead.id}`)} data-id="element-427">
                <td className="px-4 whitespace-nowrap" onClick={e => e.stopPropagation()} data-id="element-428">
                  <input type="checkbox" className="rounded border-white/20 bg-growthly-bg text-growthly-amber focus:ring-growthly-amber" data-id="element-429" />
                </td>
                <td className="px-4" data-id="element-430">
                  <div className="flex flex-col justify-center h-full" data-id="element-431">
                    <span className="text-[14px] font-semibold text-growthly-text group-hover:underline" data-id="element-432">
                      {lead.NOMBRE_NEGOCIO}
                    </span>
                    <span className="text-[12px] text-growthly-muted" data-id="element-433">
                      {lead.DIRECCION.ciudad} • {lead.LEAD_SOURCE}
                    </span>
                  </div>
                </td>
                <td className="px-4" data-id="element-434">
                  <div className="flex items-center justify-between" data-id="element-435">
                    <div className="flex flex-col" data-id="element-436">
                      <span className="text-[13px] text-growthly-text" data-id="element-437">
                        {lead.DECISOR_NOMBRE}
                      </span>
                      <div className="flex items-center text-[12px] text-growthly-muted mt-0.5" data-id="element-438">
                        <span data-id="element-439">{maskPhone(lead.TELEFONO_WHATSAPP)}</span>
                        {lead.VERIFICADO ? <CheckCircle2 className="w-3 h-3 text-growthly-green ml-1" data-id="element-440" /> : <AlertCircle className="w-3 h-3 text-growthly-red ml-1" data-id="element-441" />}
                      </div>
                    </div>
                    {/* Inline actions on hover */}
                    <div className="hidden group-hover:flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()} data-id="element-442">
                      <button className="p-1.5 text-growthly-muted hover:text-growthly-text hover:bg-white/10 rounded" data-id="element-443">
                        <Phone className="w-4 h-4" data-id="element-444" />
                      </button>
                      <button className="p-1.5 text-growthly-muted hover:text-growthly-text hover:bg-white/10 rounded" data-id="element-445">
                        <MessageSquare className="w-4 h-4" data-id="element-446" />
                      </button>
                      <button className="p-1.5 text-growthly-muted hover:text-growthly-text hover:bg-white/10 rounded" data-id="element-447">
                        <CalendarDays className="w-4 h-4" data-id="element-448" />
                      </button>
                    </div>
                  </div>
                </td>
                <td className="px-4 whitespace-nowrap" data-id="element-449">
                  <div className="flex items-center space-x-3" data-id="element-450">
                    <IcpBadge priority={lead.ICP_PRIORITY} data-id="element-451" />
                    <div className="flex flex-col w-16" data-id="element-452">
                      <span className="text-[13px] font-medium text-growthly-text" data-id="element-453">
                        {lead.LEAD_SCORE}
                      </span>
                      <div className="w-full h-1.5 bg-white/10 rounded-full mt-1" data-id="element-454">
                        <div className="h-1.5 bg-growthly-muted rounded-full" style={{
                      width: `${Math.min(lead.LEAD_SCORE / 100 * 100, 100)}%`
                    }} data-id="element-455"></div>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 whitespace-nowrap" data-id="element-456">
                  <div className="flex flex-col items-start" data-id="element-457">
                    <StateBadge state={lead.STATE} data-id="element-458" />
                    {lead.MICRO_ACCION_TYPE && <span className="text-[11px] text-growthly-muted mt-1" data-id="element-459">
                        {lead.MICRO_ACCION_TYPE}
                      </span>}
                  </div>
                </td>
                <td className="px-4 whitespace-nowrap" data-id="element-460">
                  <div className="flex items-center space-x-2" data-id="element-461">
                    <div className="w-6 h-6 rounded-full bg-growthly-panel flex items-center justify-center text-growthly-muted" data-id="element-462">
                      <User className="w-3 h-3" data-id="element-463" />
                    </div>
                    <span className="text-[13px] text-growthly-text" data-id="element-464">
                      {formatDate(lead.NEXT_ACTION_DATE)}
                    </span>
                  </div>
                </td>
                <td className="px-4 whitespace-nowrap" data-id="element-465">
                  <div className="flex items-center space-x-2" data-id="element-466">
                    <span className="text-[13px] font-medium text-growthly-text w-8" data-id="element-467">
                      {lead.EVIDENCE_COMPLETENESS}%
                    </span>
                    <div className="w-16 h-2 bg-white/10 rounded-full" data-id="element-468">
                      <div className={`h-2 rounded-full ${lead.EVIDENCE_COMPLETENESS === 100 ? 'bg-growthly-green' : 'bg-growthly-text'}`} style={{
                    width: `${lead.EVIDENCE_COMPLETENESS}%`
                  }} data-id="element-469"></div>
                    </div>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-3 border-t border-white/5 flex items-center justify-between sm:px-6 bg-growthly-panel" data-id="element-470">
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-471">
          <div data-id="element-472">
            <p className="text-[13px] text-growthly-muted" data-id="element-473">
              Mostrando{' '}
              <span className="font-medium text-growthly-text" data-id="element-474">1</span> a{' '}
              <span className="font-medium text-growthly-text" data-id="element-475">
                {leads.length}
              </span>{' '}
              de{' '}
              <span className="font-medium text-growthly-text" data-id="element-476">
                {leads.length}
              </span>{' '}
              resultados
            </p>
          </div>
          <div data-id="element-477">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-478">
              <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-white/10 bg-growthly-surface text-[13px] font-medium text-growthly-muted hover:bg-white/5" data-id="element-479">
                Anterior
              </button>
              <button className="relative inline-flex items-center px-4 py-2 border border-white/10 bg-white/10 text-[13px] font-medium text-growthly-text" data-id="element-480">
                1
              </button>
              <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-white/10 bg-growthly-surface text-[13px] font-medium text-growthly-muted hover:bg-white/5" data-id="element-481">
                Siguiente
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>;
}